/**
 * Detailed Tour Steps — on-demand per-area deep dives.
 * Copy rules: ≤70 words, second person, jargon explained on first use,
 * comparative framing, never assumes populated data.
 */
import type { TutorialStep } from "./types";

export const tutorialSteps: TutorialStep[] = [
  // ─── MAIN DASHBOARD ───
  {
    id: "dashboard-welcome",
    area: "dashboard",
    tier: "detailed",
    title: "Your home base",
    description:
      "This is where you start each day. Your revenue, prices, bookings, and occupancy all live on this one screen, so you can see how your hotel is doing in seconds.",
    targetSelector: '[data-tutorial="dashboard-header"]',
    cardPosition: "bottom",
    route: "/dashboard",
    scrollIntoView: true,
  },
  {
    id: "dashboard-revenue-chart",
    area: "dashboard",
    tier: "detailed",
    title: "Track revenue against your goals",
    description:
      "Compare this year's revenue to your budget and to last year, month by month. Use the dropdowns to switch between revenue, occupancy, ADR (average daily rate), and RevPAR (revenue per available room).",
    targetSelector: '[data-tutorial="revenue-chart"]',
    cardPosition: "right",
    route: "/dashboard",
    scrollIntoView: true,
  },
  {
    id: "dashboard-revenue-table",
    area: "dashboard",
    tier: "detailed",
    title: "The exact numbers",
    description:
      "The same information as the chart, in table form. Handy when you need precise figures for a budget meeting or an owner report.",
    targetSelector: '[data-tutorial="revenue-table"]',
    cardPosition: "top",
    route: "/dashboard",
    scrollIntoView: true,
  },
  {
    id: "dashboard-pricing-chart",
    area: "dashboard",
    tier: "detailed",
    title: "See how your price compares",
    description:
      "The black line is your room price. The colored lines are your competitors. You can instantly see how your price compares to the market on any date.",
    targetSelector: '[data-tutorial="pricing-chart"]',
    cardPosition: "top",
    route: "/dashboard",
    scrollIntoView: true,
  },
  {
    id: "dashboard-pickup-report",
    area: "dashboard",
    tier: "detailed",
    title: "Watch bookings come in",
    description:
      "Pickup shows how many new bookings arrived for future dates. Strong pickup means demand is building. Weak pickup can mean it's time to adjust your price or marketing.",
    targetSelector: '[data-tutorial="pickup-report"]',
    cardPosition: "top",
    route: "/dashboard",
    scrollIntoView: true,
  },
  {
    id: "dashboard-pacing",
    area: "dashboard",
    tier: "detailed",
    title: "Ahead or behind last year?",
    description:
      "Click the expand button on the Pickup Report to see Pacing: whether bookings are coming in faster or slower than last year. Green means ahead, red means behind.",
    targetSelector: '[data-tutorial="pickup-report"]',
    cardPosition: "top",
    route: "/dashboard",
    scrollIntoView: true,
  },
  {
    id: "dashboard-mini-calendar",
    area: "dashboard",
    tier: "detailed",
    title: "Check any date",
    description:
      "Click a date to see that day's occupancy, average rate, and revenue. The colors show how each day is pacing, so busy and quiet periods stand out at a glance.",
    targetSelector: '[data-tutorial="mini-calendar"]',
    cardPosition: "top",
    route: "/dashboard",
    scrollIntoView: true,
  },

  // ─── AI COPILOT ───
  {
    id: "ai-copilot-intro",
    area: "ai-copilot",
    tier: "detailed",
    title: "Your assistant, always on",
    description:
      "This button opens your Autumn assistant. Ask it anything about your hotel — performance, prices, marketing — and it answers using your real data, any time of day.",
    targetSelector: '[data-tutorial="ai-copilot-button"]',
    cardPosition: "left",
    route: "/dashboard",
    chatbotState: "closed",
    scrollIntoView: false,
  },
  {
    id: "ai-copilot-chat",
    area: "ai-copilot",
    tier: "detailed",
    title: "Just ask, in plain words",
    description:
      "Try \"How does next week look?\" or \"How is my marketing performing?\" You can type or use the microphone. It reads your live data and answers in seconds.",
    targetSelector: '[data-tutorial="ai-copilot-chat"]',
    cardPosition: "left",
    route: "/dashboard",
    chatbotState: "open",
    chatMode: "chat",
    scrollIntoView: false,
    waitForElement: true,
  },
  {
    id: "ai-copilot-agent-mode",
    area: "ai-copilot",
    tier: "detailed",
    title: "Agent mode: make changes by chatting",
    description:
      "Switch to Agent mode to take action: \"Bump up my weekend rates\" or \"Add a 15% premium for the holiday weekend.\" It always shows you the change and asks for your approval before touching anything.",
    targetSelector: '[data-tutorial="chat-mode-toggle"]',
    cardPosition: "left",
    route: "/dashboard",
    chatbotState: "open",
    chatMode: "agent",
    scrollIntoView: false,
    skipIfTargetMissing: true,
  },
  {
    id: "ai-copilot-voice",
    area: "ai-copilot",
    tier: "detailed",
    title: "Talk instead of type",
    description:
      "Prefer speaking? Start a live voice conversation and talk through your questions hands-free, like a phone call with your revenue assistant.",
    targetSelector: '[data-tutorial="chat-talk"]',
    cardPosition: "left",
    route: "/dashboard",
    chatbotState: "open",
    scrollIntoView: false,
    featureFlag: "chatVoice",
    skipIfTargetMissing: true,
  },
  {
    id: "ai-copilot-support",
    area: "ai-copilot",
    tier: "detailed",
    title: "Reach our team from here",
    description:
      "Our team sees the questions you ask, and the question-mark button sends them a message directly. It's the fastest way to get human help — much faster than email.",
    targetSelector: '[data-tutorial="chat-help"]',
    cardPosition: "left",
    route: "/dashboard",
    chatbotState: "open",
    scrollIntoView: false,
    waitForElement: true,
  },

  // ─── SETTINGS ───
  {
    id: "settings-tabs",
    area: "settings",
    tier: "detailed",
    title: "Where you shape the agent",
    description:
      "Settings control how Autumn prices your hotel. The three that matter most: Seasons (your price limits), Competitor Set (who you compete with), and AI CoPilot (your standing instructions).",
    targetSelector: '[data-tutorial="settings-tabs"]',
    cardPosition: "bottom",
    route: "/settings",
    scrollIntoView: true,
    waitForElement: true,
  },
  {
    id: "settings-seasons",
    area: "settings",
    tier: "detailed",
    title: "Your price guardrails",
    description:
      "Set the lowest and highest price you're comfortable with for each season. The agent optimizes freely inside your limits and never prices outside them. One-off events? Handle those in the Calendar.",
    targetSelector: '[data-tutorial="season-settings"]',
    cardPosition: "right",
    route: "/settings?tab=seasons",
    scrollIntoView: true,
  },
  {
    id: "settings-competitors",
    area: "settings",
    tier: "detailed",
    title: "Who you're really competing with",
    description:
      "Rank your competitors from closest to furthest. This teaches the agent who your guests actually compare you against, so its pricing reflects your real market.",
    targetSelector: '[data-tutorial="competitor-list"]',
    cardPosition: "right",
    route: "/settings?tab=competitor&subtab=competitors",
    scrollIntoView: true,
  },
  {
    id: "settings-unique-selling",
    area: "settings",
    tier: "detailed",
    title: "What makes you special",
    description:
      "Tell Autumn what sets your hotel apart — a rooftop bar, beachfront access, boutique charm. These help the agent understand when you can price above the competition.",
    targetSelector: '[data-tutorial="unique-selling-points"]',
    cardPosition: "right",
    route: "/settings?tab=competitor&subtab=unique-selling",
    scrollIntoView: true,
  },
  {
    id: "settings-physical-quality",
    area: "settings",
    tier: "detailed",
    title: "Your property, compared",
    description:
      "Rate your hotel's physical condition against each competitor. A freshly renovated property can charge more; an older one may compete on value. The agent factors this in.",
    targetSelector: '[data-tutorial="physical-quality"]',
    cardPosition: "right",
    route: "/settings?tab=competitor&subtab=physical-quality",
    scrollIntoView: true,
  },
  {
    id: "settings-location",
    area: "settings",
    tier: "detailed",
    title: "Location, compared",
    description:
      "Compare your location to each competitor's. Closer to the beach, center, or airport usually means guests will pay more — and the agent prices accordingly.",
    targetSelector: '[data-tutorial="location-comparison"]',
    cardPosition: "right",
    route: "/settings?tab=competitor&subtab=location",
    scrollIntoView: true,
  },
  {
    id: "settings-pricing",
    area: "settings",
    tier: "detailed",
    title: "Where you want to sit",
    description:
      "Set where you expect your price to land against each competitor — above, below, or level. This calibrates the agent to your strategy: premium, mid-range, or value.",
    targetSelector: '[data-tutorial="pricing-comparison"]',
    cardPosition: "right",
    route: "/settings?tab=competitor&subtab=pricing",
    scrollIntoView: true,
  },
  {
    id: "settings-budget",
    area: "settings",
    tier: "detailed",
    title: "Your revenue goals",
    description:
      "Enter your monthly revenue budget here. Your dashboard then compares actual revenue against it all year, so you always know if you're on plan.",
    targetSelector: '[data-tutorial="settings-tabs"]',
    cardPosition: "bottom",
    route: "/settings?tab=budget",
    scrollIntoView: true,
    waitForElement: true,
  },
  {
    id: "settings-ai-instructions",
    area: "settings",
    tier: "detailed",
    title: "Standing instructions",
    description:
      "Give the agent lasting rules and context, like \"never discount below competitors\" or \"prioritize occupancy in winter.\" The more it knows about how you run your hotel, the better its decisions.",
    targetSelector: '[data-tutorial="ai-chat-settings"]',
    cardPosition: "right",
    route: "/settings?tab=ai-chat&subtab=instructions",
    scrollIntoView: true,
  },
  {
    id: "settings-ai-documents",
    area: "settings",
    tier: "detailed",
    title: "Give it your documents",
    description:
      "Upload documents the assistant should know — policies, fact sheets, house rules. It uses them when answering questions and making recommendations.",
    targetSelector: '[data-tutorial="settings-tabs"]',
    cardPosition: "bottom",
    route: "/settings?tab=ai-chat&subtab=documents",
    scrollIntoView: true,
    waitForElement: true,
  },
  {
    id: "settings-mlos",
    area: "settings",
    tier: "detailed",
    roles: ["U", "Admin"],
    title: "Minimum-stay rules",
    description:
      "Have busy dates where one-night stays cost you money? Set minimum-stay rules — like two nights over weekends — and Autumn applies them for you.",
    targetSelector: '[data-tutorial="mlos-settings"]',
    cardPosition: "right",
    route: "/settings?tab=mlos",
    scrollIntoView: true,
    waitForElement: true,
    skipIfTargetMissing: true,
  },
  {
    id: "settings-email-digest",
    area: "settings",
    tier: "detailed",
    roles: ["U", "Admin"],
    title: "Your numbers, delivered",
    description:
      "The Email Digest sends your key numbers to your inbox on the schedule you choose — stay on top of performance without logging in.",
    targetSelector: '[data-tutorial="email-digest-settings"]',
    cardPosition: "right",
    route: "/settings?tab=email-digest",
    scrollIntoView: true,
    waitForElement: true,
    skipIfTargetMissing: true,
  },
  {
    id: "settings-billing",
    area: "settings",
    tier: "detailed",
    title: "Your billing, transparent",
    description:
      "See your billing history, what each charge covers, and download invoices — no need to email us for a copy.",
    targetSelector: '[data-tutorial="settings-tabs"]',
    cardPosition: "bottom",
    route: "/settings?tab=billing",
    scrollIntoView: true,
    waitForElement: true,
  },

  // ─── CALENDAR ───
  {
    id: "calendar-overview",
    area: "calendar",
    tier: "detailed",
    title: "Day-by-day control",
    description:
      "Your day-by-day view of prices and availability. Override the agent for specific dates, adjust room-type price gaps, and make bulk changes — all from one screen. You can also chat with the pricing agent right here.",
    targetSelector: '[data-tutorial="calendar-view"]',
    cardPosition: "top",
    route: "/calendar",
    scrollIntoView: true,
    waitForElement: true,
  },
  {
    id: "calendar-override-prices",
    area: "calendar",
    tier: "detailed",
    title: "Set your own price",
    description:
      "Click any date to set the price yourself. Perfect when you know something the agent doesn't — a local concert, a convention, a holiday rush.",
    targetSelector: '[data-tutorial="calendar-pricing"]',
    cardPosition: "bottom",
    route: "/calendar",
    scrollIntoView: true,
  },
  {
    id: "calendar-differentials",
    area: "calendar",
    tier: "detailed",
    title: "Keep room prices in proportion",
    description:
      "Control the price gap between room types — for example, suites always 40% above standard rooms. Your price structure stays logical while the agent moves base rates.",
    targetSelector: '[data-tutorial="room-types"]',
    cardPosition: "right",
    route: "/calendar",
    scrollIntoView: true,
  },
  {
    id: "calendar-bulk-actions",
    area: "calendar",
    tier: "detailed",
    title: "Change many days at once",
    description:
      "Select a whole week or month and apply changes in bulk — much faster than clicking day by day for seasonal adjustments or promotions.",
    targetSelector: '[data-tutorial="calendar-view"]',
    cardPosition: "top",
    route: "/calendar",
    scrollIntoView: true,
  },

  // ─── MARKETING ───
  {
    id: "marketing-overview",
    area: "marketing",
    tier: "detailed",
    title: "How ads become bookings",
    description:
      "This page tracks the whole guest journey: how many people saw your ads, how many visited your site, and how many booked.",
    targetSelector: '[data-tutorial="marketing-header"]',
    cardPosition: "bottom",
    route: "/marketing",
    scrollIntoView: true,
    waitForElement: true,
  },
  {
    id: "marketing-trends",
    area: "marketing",
    tier: "detailed",
    title: "Your numbers over time",
    description:
      "Watch how your marketing moves day by day. One number deserves special attention: Search Impression Share — how often YOU appear when travelers search your hotel's name, versus booking sites appearing instead. A low share means booking sites are capturing guests who were looking for you.",
    targetSelector: "#trends-over-time",
    cardPosition: "right",
    route: "/marketing?tab=dashboard",
    scrollIntoView: true,
  },
  {
    id: "marketing-funnel",
    area: "marketing",
    tier: "detailed",
    title: "Where guests drop off",
    description:
      "The funnel shows every stage from seeing your ad to booking, with the percentage that continues at each step. If one stage leaks, you know exactly where to look.",
    targetSelector: "#funnel-summary, #funnel-loading",
    cardPosition: "top",
    route: "/marketing?tab=dashboard",
    scrollIntoView: true,
  },
  {
    id: "marketing-campaign-categories",
    area: "marketing",
    tier: "detailed",
    title: "What each campaign does",
    description:
      "Brand Protection keeps you at the top when travelers search your hotel's name, so booking sites and competitors can't capture them. Discovery reaches travelers who haven't heard of you. High Intent Search targets travelers actively choosing a hotel like yours, and Remarketing brings back visitors who didn't book the first time.",
    targetSelector: "#campaign-summary",
    cardPosition: "top",
    route: "/marketing?tab=dashboard",
    scrollIntoView: true,
  },
  {
    id: "marketing-devices-markets",
    area: "marketing",
    tier: "detailed",
    title: "Who books, and how they find you",
    description:
      "Device Summary shows whether guests book on phone or desktop. Market Insights shows the locations tied to your ad viewers — where they've been, where they're often located, or what they searched, not necessarily where they live.",
    targetSelector: "#device-summary",
    cardPosition: "top",
    route: "/marketing?tab=dashboard",
    scrollIntoView: true,
  },
  {
    id: "marketing-insights",
    area: "marketing",
    tier: "detailed",
    title: "Your automatic briefing",
    description:
      "Short notes from Autumn about what's working and what changed — like a quick update from your marketing team, refreshed automatically.",
    targetSelector: '[data-tutorial="insights-panel"]',
    cardPosition: "left",
    route: "/marketing?tab=dashboard",
    scrollIntoView: true,
  },
  {
    id: "marketing-traffic",
    area: "marketing",
    tier: "detailed",
    title: "Where visitors come from",
    description:
      "See what brings people to your website — Google searches, social media, direct visits, or other sites — so you know which channels earn their keep.",
    targetSelector: '[data-tutorial="marketing-content"]',
    cardPosition: "top",
    route: "/marketing?tab=traffic",
    scrollIntoView: true,
  },
  {
    id: "marketing-settings",
    area: "marketing",
    tier: "detailed",
    roles: ["MarketingOnly"],
    title: "Reports and billing",
    description:
      "Set up your email reports, open your monthly report, and manage billing — all in one place.",
    targetSelector: '[data-tutorial="marketing-content"]',
    cardPosition: "top",
    route: "/marketing?tab=settings",
    scrollIntoView: true,
  },

  // ─── REPUTATION ─── (copy current where noted; light refresh elsewhere)
  {
    id: "reputation-connect",
    area: "reputation",
    tier: "detailed",
    title: "Connect your hotel",
    description:
      "First, let's connect your hotel to start importing reviews. Search for your property — once connected, reviews from Google, Booking.com, and Expedia import automatically. After connecting, re-run this tour for the full walkthrough!",
    targetSelector: '[data-tutorial="reputation-connect-overlay"]',
    cardPosition: "right",
    route: "/reputation?tab=dashboard",
    scrollIntoView: true,
    waitForElement: true,
    skipIfTargetMissing: true,
  },
  {
    id: "reputation-overview",
    area: "reputation",
    tier: "detailed",
    title: "All your reviews, one place",
    description:
      "Every guest review from Google, Booking.com, and Expedia in one place — no more hopping between sites. Use the Dashboard tab for insights and the All Reviews tab to read and respond.",
    targetSelector: '[data-tutorial="reputation-header"]',
    cardPosition: "bottom",
    route: "/reputation?tab=dashboard",
    scrollIntoView: true,
    waitForElement: true,
    skipIfElementExists: '[data-tutorial="reputation-connect-overlay"]',
  },
  {
    id: "reputation-kpi-row",
    area: "reputation",
    tier: "detailed",
    title: "Your reputation at a glance",
    description:
      "Five numbers that tell you how your reputation is doing. Overall Rating averages all channels (10-point scores convert to 5). Pending Responses shows reviews awaiting a reply — click it to jump straight to the urgent ones. Response Rate and Avg Response Time show how consistently you engage.",
    targetSelector: '[data-tutorial="reputation-kpi-row"]',
    cardPosition: "bottom",
    route: "/reputation?tab=dashboard",
    scrollIntoView: true,
    skipIfElementExists: '[data-tutorial="reputation-connect-overlay"]',
  },
  {
    id: "reputation-ask-ai",
    area: "reputation",
    tier: "detailed",
    title: "Ask AI about your reviews",
    description:
      "Wondering what guests are saying? Type a question — like \"What do guests complain about most?\" — and the AI reads your reviews and summarizes. The suggestion chips below get you started instantly.",
    targetSelector: '[data-tutorial="reputation-ask-ai"]',
    cardPosition: "right",
    route: "/reputation?tab=dashboard",
    scrollIntoView: true,
    skipIfElementExists: '[data-tutorial="reputation-connect-overlay"]',
  },
  {
    id: "reputation-priority-inbox",
    area: "reputation",
    tier: "detailed",
    title: "What needs you first",
    description:
      "Reviews that need attention, sorted by urgency — negative reviews and those unanswered over 4 hours come first. Toggle Priority or Recent sorting, and click any review to open and respond.",
    targetSelector: '[data-tutorial="reputation-priority-inbox"]',
    cardPosition: "right",
    route: "/reputation?tab=dashboard",
    scrollIntoView: true,
    skipIfElementExists: '[data-tutorial="reputation-connect-overlay"]',
  },
  {
    id: "reputation-sentiment-chart",
    area: "reputation",
    tier: "detailed",
    title: "The mood of your reviews",
    description:
      "The split between positive, neutral, and negative reviews. Use the period filter (7d / 30d / 90d / 180d / All) to see how sentiment shifts. A sudden spike in negatives can flag a service issue worth investigating.",
    targetSelector: '[data-tutorial="reputation-sentiment-chart"]',
    cardPosition: "left",
    route: "/reputation?tab=dashboard",
    scrollIntoView: true,
    skipIfElementExists: '[data-tutorial="reputation-connect-overlay"]',
  },
  {
    id: "reputation-channel-chart",
    area: "reputation",
    tier: "detailed",
    title: "Reviews by channel",
    description:
      "How reviews split across Google, Booking.com, and Expedia. Toggle Score (average rating per channel) or Count (volume). If one channel lags, that's where to focus your responses first.",
    targetSelector: '[data-tutorial="reputation-channel-chart"]',
    cardPosition: "left",
    route: "/reputation?tab=dashboard",
    scrollIntoView: true,
    skipIfElementExists: '[data-tutorial="reputation-connect-overlay"]',
  },
  {
    id: "reputation-themes",
    area: "reputation",
    tier: "detailed",
    title: "What guests keep mentioning",
    description:
      "The AI extracts recurring themes — What Guests Love and Areas to Improve. Click a theme to see the reviews behind it. If \"breakfast\" keeps appearing in complaints, you know where to focus.",
    targetSelector: '[data-tutorial="reputation-themes"]',
    cardPosition: "top",
    route: "/reputation?tab=dashboard",
    scrollIntoView: true,
    skipIfElementExists: '[data-tutorial="reputation-connect-overlay"]',
  },
  {
    id: "reputation-filters",
    area: "reputation",
    tier: "detailed",
    title: "Find exactly what you need",
    description:
      "Narrow reviews by channel, rating, sentiment, response status, or date. \"Urgent\" under Status shows unanswered negative reviews. Combine filters — like all 1-4 star Google reviews from the last 30 days.",
    targetSelector: '[data-tutorial="reputation-filters"]',
    cardPosition: "bottom",
    route: "/reputation?tab=all-reviews",
    scrollIntoView: true,
    waitForElement: true,
    skipIfElementExists: '[data-tutorial="reputation-connect-overlay"]',
  },
  {
    id: "reputation-review-list",
    area: "reputation",
    tier: "detailed",
    title: "Browse every review",
    description:
      "Each card shows the channel, star rating, sentiment, and a preview. Click any review to open the full detail panel, read everything, and craft a response.",
    targetSelector: '[data-tutorial="reputation-review-list"]',
    cardPosition: "right",
    route: "/reputation?tab=all-reviews",
    scrollIntoView: true,
    skipIfElementExists: '[data-tutorial="reputation-connect-overlay"]',
  },
  {
    id: "reputation-how-to-respond",
    area: "reputation",
    tier: "detailed",
    title: "Respond in seconds",
    description:
      "Open a review and click Generate Suggestions to have AI draft a personalized reply. Edit it to add your personal touch, then publish. Expedia responses send automatically; for Google and Booking.com, your reply is copied and the platform opens so you can paste it.",
    targetSelector: '[data-tutorial="reputation-kpi-row"]',
    cardPosition: "bottom",
    route: "/reputation?tab=dashboard",
    scrollIntoView: true,
    skipIfElementExists: '[data-tutorial="reputation-connect-overlay"]',
  },
  {
    id: "reputation-response-tips",
    area: "reputation",
    tier: "detailed",
    title: "Responses that win guests back",
    description:
      "Respond to negative reviews within 24 hours — speed shows you care. Personalize AI suggestions; guests can tell when a reply is generic. Address the specific complaint, offer a fix, invite them back. Thank positive reviewers too — future guests notice.",
    targetSelector: '[data-tutorial="reputation-priority-inbox"]',
    cardPosition: "right",
    route: "/reputation?tab=dashboard",
    scrollIntoView: true,
    skipIfElementExists: '[data-tutorial="reputation-connect-overlay"]',
  },
  {
    id: "reputation-settings",
    area: "reputation",
    tier: "detailed",
    title: "Switch your connected hotel",
    description:
      "Click the gear icon to change your connected hotel anytime. Search for the new property and reviews re-import — your previous hotel's data is preserved.",
    targetSelector: '[data-tutorial="reputation-settings"]',
    cardPosition: "left",
    route: "/reputation?tab=dashboard",
    scrollIntoView: true,
    skipIfElementExists: '[data-tutorial="reputation-connect-overlay"]',
  },

  // ─── GROUP BOOKINGS ───
  {
    id: "groups-overview",
    area: "groups",
    tier: "detailed",
    title: "Group quotes in seconds",
    description:
      "When someone calls about a block of rooms, generate a smart quote in seconds — no spreadsheets, no manual rate math.",
    targetSelector: '[data-tutorial="groups-header"]',
    cardPosition: "bottom",
    route: "/groups",
    scrollIntoView: true,
    waitForElement: true,
  },
  {
    id: "groups-create",
    area: "groups",
    tier: "detailed",
    title: "Create a quote",
    description:
      "Fill in rooms, dates, and room types. Autumn calculates the best rate from your availability, market conditions, and pricing strategy — then generates a professional proposal you can send.",
    targetSelector: '[data-tutorial="groups-content"]',
    cardPosition: "top",
    route: "/groups",
    scrollIntoView: true,
  },
  {
    id: "groups-history",
    area: "groups",
    tier: "detailed",
    title: "Every quote, saved",
    description:
      "All past quotes live in the left sidebar. Click one to review it, resend it, or create a new version with updated pricing.",
    targetSelector: '[data-tutorial="groups-sidebar"]',
    cardPosition: "right",
    route: "/groups",
    scrollIntoView: true,
  },

  // ─── BOOKING ENGINE ───
  {
    id: "booking-engine-overview",
    area: "booking-engine",
    tier: "detailed",
    roles: ["U", "Admin"],
    title: "Take bookings directly",
    description:
      "Your booking engine takes reservations on your own website — commission-free. Guests book with you directly instead of through booking sites.",
    targetSelector: '[data-tutorial="booking-engine-content"]',
    cardPosition: "top",
    route: "/booking-engine",
    scrollIntoView: true,
    waitForElement: true,
  },
];
